<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import QuoteBreakdown from '../QuoteBreakdown.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const carBooking = useCarBookingStore();

const session = computed(() => carBooking.sessionFor(props.conversationId));
const offer = computed(() =>
  (session.value.offers || []).find(o => o.token === session.value.detailToken)
);
const vehicle = computed(() => offer.value?.vehicle || {});
const breakdown = computed(() => session.value.detailQuote);
const uiFlags = computed(() => carBooking.uiFlags);

const unavailable = ref(false);
const errorMessage = ref(null);
const isImageExpanded = ref(false);

const openImage = () => {
  if (!vehicle.value.imageUrl) return;
  isImageExpanded.value = true;
};
const closeImage = () => {
  isImageExpanded.value = false;
};

const onKeydown = e => {
  if (e.key === 'Escape') {
    // Swallow Escape so the booking modal's own Escape-to-close (a window
    // keydown listener) doesn't fire — closing the lightbox should not also
    // tear down the whole modal.
    e.stopPropagation();
    closeImage();
  }
};

watch(isImageExpanded, expanded => {
  if (expanded) {
    document.addEventListener('keydown', onKeydown);
  } else {
    document.removeEventListener('keydown', onKeydown);
  }
});

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown));

const isUnavailable = computed(
  () => unavailable.value || breakdown.value?.outcome === 'unavailable'
);

const back = () => carBooking.closeDetail(props.conversationId);

const sendQuote = async () => {
  const res = await carBooking.shareQuote(props.conversationId, {
    token: session.value.detailToken,
  });
  if (res?.outcome) {
    unavailable.value = true;
    useAlert(t('CONVERSATION.CAR_BOOKING.DETAIL.UNAVAILABLE'));
    return;
  }
  useAlert(t('CONVERSATION.CAR_BOOKING.DETAIL.QUOTE_SENT'));
};

onMounted(async () => {
  if (!session.value.detailToken) return;
  const res = await carBooking.quotePreview(props.conversationId, {
    token: session.value.detailToken,
  });
  unavailable.value = res?.outcome === 'unavailable';
  if (res?.error !== undefined) {
    errorMessage.value =
      res.error || t('CONVERSATION.CAR_BOOKING.DETAIL.QUOTE_ERROR');
  }
});
</script>

<template>
  <div
    v-if="!offer"
    class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-center text-sm text-n-slate-11"
  >
    {{ t('CONVERSATION.CAR_BOOKING.DETAIL.EMPTY') }}
  </div>

  <div v-else class="flex h-full flex-col">
    <div class="flex flex-1 flex-col gap-4 overflow-y-auto">
      <div
        class="overflow-hidden rounded-xl border border-n-weak bg-n-surface-1"
      >
        <div class="flex items-center gap-3.5 p-3.5">
          <div
            class="flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-n-alpha-2 text-n-slate-11"
            :class="{ 'cursor-zoom-in': vehicle.imageUrl }"
            @click="openImage"
          >
            <img
              v-if="vehicle.imageUrl"
              :src="vehicle.imageUrl"
              :alt="vehicle.sampleModel || offer.category"
              class="h-full w-full object-contain"
            />
            <span v-else class="i-lucide-car size-8" />
          </div>
          <div class="flex min-w-0 flex-col gap-0.5">
            <span class="truncate text-sm font-semibold text-n-slate-12">
              {{ vehicle.sampleModel || offer.category }}
            </span>
            <span class="truncate text-sm text-n-slate-11">
              <span>{{ offer.category }}</span>
              <span class="before:mx-1 before:content-['·']">{{
                offer.provider
              }}</span>
            </span>
          </div>
        </div>

        <div
          class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-n-weak px-3.5 py-2.5 text-xs text-n-slate-11"
        >
          <span v-if="vehicle.seats" class="inline-flex items-center gap-1">
            <span class="i-lucide-users size-3.5" />{{ vehicle.seats }}
          </span>
          <span v-if="vehicle.bags" class="inline-flex items-center gap-1">
            <span class="i-lucide-briefcase size-3.5" />{{ vehicle.bags }}
          </span>
          <span v-if="vehicle.doors" class="inline-flex items-center gap-1">
            <span class="i-lucide-door-open size-3.5" />{{ vehicle.doors }}
          </span>
          <span
            v-if="vehicle.transmission"
            class="inline-flex items-center gap-1"
          >
            <span class="i-lucide-settings-2 size-3.5" />
            <span class="capitalize">{{
              vehicle.transmission.toLowerCase()
            }}</span>
          </span>
          <span v-if="vehicle.fuel" class="inline-flex items-center gap-1">
            <span class="i-lucide-fuel size-3.5" />
            <span class="capitalize">{{ vehicle.fuel.toLowerCase() }}</span>
          </span>
          <span v-if="vehicle.airCon" class="inline-flex items-center gap-1">
            <span class="i-lucide-snowflake size-3.5" />{{
              t('CONVERSATION.CAR_BOOKING.OFFERS.AC')
            }}
          </span>
          <span
            v-if="offer.unlimitedKm"
            class="inline-flex items-center gap-1 text-n-green-11"
          >
            <span class="i-lucide-infinity size-3.5" />{{
              t('CONVERSATION.CAR_BOOKING.DETAIL.UNLIMITED_KM')
            }}
          </span>
          <span v-if="vehicle.acriss" class="text-n-slate-10">
            {{
              t('CONVERSATION.CAR_BOOKING.DETAIL.ACRISS', {
                code: vehicle.acriss,
              })
            }}
          </span>
        </div>
      </div>

      <div
        v-if="uiFlags.quoting"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <Spinner :size="16" />
        <span>{{ t('CONVERSATION.CAR_BOOKING.DETAIL.LOADING_PRICE') }}</span>
      </div>

      <div
        v-else-if="isUnavailable"
        class="rounded-lg border border-n-amber-8 bg-n-amber-9/10 px-3 py-2.5 text-sm text-n-slate-12"
      >
        {{ t('CONVERSATION.CAR_BOOKING.DETAIL.UNAVAILABLE') }}
      </div>

      <QuoteBreakdown v-else-if="breakdown" :quote="breakdown" />

      <div
        v-else-if="errorMessage"
        class="rounded-lg border border-n-ruby-8 bg-n-ruby-9/10 px-3 py-2.5 text-sm text-n-slate-12"
      >
        {{ errorMessage }}
      </div>
    </div>

    <div
      class="mt-4 flex items-center gap-3 border-t border-n-weak bg-n-surface-2 px-4 py-3"
    >
      <NextButton
        faded
        slate
        :label="t('CONVERSATION.CAR_BOOKING.DETAIL.BACK')"
        @click="back"
      />
      <NextButton
        solid
        blue
        class="ml-auto"
        :label="t('CONVERSATION.CAR_BOOKING.DETAIL.SEND_QUOTE')"
        :disabled="uiFlags.sharingQuote || !breakdown || isUnavailable"
        :is-loading="uiFlags.sharingQuote"
        @click="sendQuote"
      />
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="isImageExpanded"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
      @click="closeImage"
    >
      <button
        class="absolute right-4 top-4 flex size-9 items-center justify-center rounded-full bg-black/40 text-white"
        @click.stop="closeImage"
      >
        <span class="i-lucide-x size-5" />
      </button>
      <img
        :src="vehicle.imageUrl"
        :alt="vehicle.sampleModel || offer.category"
        class="max-h-[80vh] max-w-[80vw] object-contain"
        @click.stop
      />
    </div>
  </Teleport>
</template>
